import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";
import { resolveAssetDisplayUrl } from "../lib/assetUrl";

function FacebookIcon({ size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        fill="currentColor"
        d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 4.997 3.657 9.128 8.438 9.88v-6.987H7.898v-2.893h2.54V9.858c0-2.522 1.492-3.915 3.777-3.915 1.094 0 2.238.197 2.238.197v2.475h-1.26c-1.242 0-1.63.776-1.63 1.572v1.887h2.773l-.443 2.893h-2.33v6.987C18.343 21.188 22 17.057 22 12.06Z"
      />
    </svg>
  );
}

function InstagramIcon({ size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        fill="currentColor"
        d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9Zm10.25 1.75a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
      />
    </svg>
  );
}

function prettyUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  try {
    const u = new URL(s);
    const host = u.hostname.replace(/^www\./, "");
    const path = u.pathname.replace(/\/$/, "");
    const suffix = `${host}${path}`;
    return suffix.length > 44 ? `${suffix.slice(0, 44)}…` : suffix;
  } catch {
    // fallback when input isn't a valid absolute URL
    const cleaned = s.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
    return cleaned.length > 44 ? `${cleaned.slice(0, 44)}…` : cleaned;
  }
}

export default function Footer() {
  const { settings } = useSettings();
  const siteName = (settings?.siteName || "").trim() || "Lòng Vòng Đà Lạt";
  const contactPersonName = (settings?.contactPersonName || "").trim();
  const hotline = (settings?.hotline || "").trim();
  const email = (settings?.email || "").trim();
  const logoSrc = resolveAssetDisplayUrl(settings?.logo?.url, settings?.logo?.key);
  const facebook = (settings?.facebook || "").trim();
  const instagram = (settings?.instagram || "").trim();

  return (
    <footer className="border-t border-slate-200 bg-gradient-to-b from-slate-950 to-slate-900 text-slate-300">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <div className="grid gap-10 md:grid-cols-[1.2fr,1fr,1fr,1.1fr]">
          <div className="space-y-4">
            <Link to="/" className="inline-flex items-center gap-3 group hover:scale-105 transition-transform">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-white/20 bg-gradient-to-br from-white/10 to-white/5 shadow-soft group-hover:border-white/40">
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <MapPin size={19} className="text-white/90" />
                )}
              </div>
              <div className="leading-tight">
                <p className="text-base font-bold text-white">{siteName}</p>
                <p className="text-xs text-slate-400 font-medium">Khám phá thành phố sương mù</p>
              </div>
            </Link>
            <p className="max-w-md text-sm leading-relaxed text-slate-400">
              Gợi ý địa điểm, trải nghiệm và hành trình khám phá Đà Lạt theo cách dễ nhất.
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 letter-spacing-wider">Liên kết chính</p>
            <div className="grid gap-2.5 text-sm">
              <Link to="/map" className="text-slate-300 hover:text-white transition-colors font-medium">
                Bản đồ
              </Link>
              <Link to="/locations" className="text-slate-300 hover:text-white transition-colors font-medium">
                Địa điểm
              </Link>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Tài khoản</p>
            <div className="grid gap-2.5 text-sm">
              <Link to="/tai-khoan" className="text-slate-300 hover:text-white transition-colors font-medium">
                Tài khoản
              </Link>
              <Link to="/tai-khoan#doi-mat-khau" className="text-slate-300 hover:text-white transition-colors font-medium">
                Đổi mật khẩu
              </Link>
              <div className="mt-1 flex flex-wrap gap-3">
                <Link to="/login" className="text-slate-300 hover:text-white transition-colors font-semibold">
                  Đăng nhập
                </Link>
                <Link to="/register" className="text-slate-300 hover:text-white transition-colors font-semibold">
                  Đăng ký
                </Link>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Kết nối với chúng tôi</p>
            <div className="space-y-2.5 text-sm text-slate-300">
              <div className="flex items-center gap-2.5 text-slate-300">
                <Phone size={17} className="text-emerald-400 flex-shrink-0" />
                <span className="font-medium">
                  {contactPersonName
                    ? `${contactPersonName} • ${hotline || "Hotline (đang cập nhật)"}`
                    : (hotline || "Hotline (đang cập nhật)")}
                </span>
              </div>
              <div className="flex items-center gap-2.5 text-slate-300">
                <Mail size={17} className="text-emerald-400 flex-shrink-0" />
                <span className="font-medium">{email || "Email (đang cập nhật)"}</span>
              </div>
            </div>

            <div className="pt-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Mạng xã hội</p>
              <div className="space-y-2">
              {facebook ? (
                <a
                  href={facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 transition hover:bg-white/10"
                >
                  <FacebookIcon size={16} className="shrink-0 text-emerald-400" />
                  <span className="min-w-0 truncate text-xs font-medium text-slate-400 group-hover:text-slate-300">
                    {prettyUrl(facebook)}
                  </span>
                </a>
              ) : null}
              {instagram ? (
                <a
                  href={instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 transition hover:bg-white/10"
                >
                  <InstagramIcon size={16} className="shrink-0 text-emerald-400" />
                  <span className="min-w-0 truncate text-xs font-medium text-slate-400 group-hover:text-slate-300">
                    {prettyUrl(instagram)}
                  </span>
                </a>
              ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {siteName}</p>
          <div />
        </div>
      </div>
    </footer>
  );
}

